import { useEffect } from "react";
import { SUBPROJECTS_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_SUBPROJECT_TYPE, PALETTE_HEX } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FieldInput, FieldTextarea, FieldSelect, FieldColor, FieldTags, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

const EMPTY_VALUES = {
    name: "", type: "FRONTEND", description: "", repositoryUrl: "", color: "#e83a52", stack: [], isFavorite: false,
};

export const SubProjectFormModal = ({ isOpen, projectId, subProject = null, subProjects = [], onClose, onSaved }) => {

    const IS_EDIT = !!subProject?.id;

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 400,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: { name: { required: true, minLength: 2 } },

        onSubmit: async (values) => {

            if (isDuplicateRecord({ rows: subProjects, value: values.name, currentId: subProject?.id })) {
                FORM.setError("name", "Ya existe un subproyecto con ese nombre", { focus: true });
                return;
            }

            const PAYLOAD = {
                projectId,
                name: values.name.trim(),
                type: values.type,
                description: values.description || undefined,
                repositoryUrl: values.repositoryUrl || undefined,
                color: values.color || undefined,
                stack: values.stack?.length > 0 ? values.stack : undefined,
                isFavorite: !!values.isFavorite,
            };

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: SUBPROJECTS_CONSTANTS.UPDATE, successMessage: "Subproyecto actualizado", id: subProject.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: SUBPROJECTS_CONSTANTS.CREATE, successMessage: "Subproyecto creado", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            onSaved?.(RESPONSE.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {

        if (!isOpen) return;

        FORM.setValues(IS_EDIT ? {
            name: subProject.name ?? "",
            type: subProject.type ?? "FRONTEND",
            description: subProject.description ?? "",
            repositoryUrl: subProject.repositoryUrl ?? "",
            color: subProject.color ?? "#e83a52",
            stack: subProject.stack ?? [],
            isFavorite: !!subProject.isFavorite,
        } : EMPTY_VALUES);

    }, [isOpen, subProject]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
                isDirty={FORM.isDirty}
            size="lg"
            title={IS_EDIT ? "Editar subproyecto" : "Nuevo subproyecto"}
            description="Cada subproyecto lleva su propia bitácora, tareas, entornos, scripts, archivos y readme."
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                        {IS_EDIT ? "Guardar cambios" : "Crear subproyecto"}
                    </AppButton>
                </>
            } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="subproject_isFavorite" label="Destacado" text="Mostrar este subproyecto primero"
                isChecked={!!FORM.values.isFavorite}
                onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <FormGrid columns={2}>

                <FieldInput {...FORM.field("name")} label="Nombre" placeholder="Ej: Frontend" />

                <FieldSelect {...FORM.field("type")} label="Tipo" options={OPTIONS_SUBPROJECT_TYPE} isSearchable />

                <FieldTextarea {...FORM.field("description")} label="Descripción" rows={3} span={2} placeholder="Ej: Portal interno de operaciones" />

                <FieldInput {...FORM.field("repositoryUrl")} label="Repositorio propio" placeholder="Ej: https://github.com/usuario/repo" />

                <FieldColor {...FORM.field("color")} label="Color" swatches={PALETTE_HEX} />


                <FieldTags {...FORM.fieldValue("stack", { emptyValue: [] })} label="Stack"
                    placeholder="Ej: react, vite" span={2} />

            </FormGrid>

        </AppModal>
    );
};
